"use client";

import { usePurchases } from "@saas/payments/hooks/purchases";
import {
	useChangeSubscriptionPlanMutation,
	useCreateCheckoutLinkMutation,
} from "@saas/payments/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { purchasesQueryKey } from "../lib/api";

interface ChangePlanButtonProps {
	userId?: string;
	organizationId?: string;
	productId: string;
	className?: string;
	targetPlanPrice?: number;
	/**
	 * 换档在 Waffo 下同样是一次跳走付款的 hosted checkout，所以它和普通结账一样
	 * 要发 checkout-clicked。事件属性（planId / interval / priceType）只有渲染处
	 * 知道，所以由 PricingTable 预先绑好传进来；返回的 promise 必须在跳转前 await，
	 * 否则 beacon 会被导航掐掉。
	 */
	trackCheckout?: () => Promise<unknown>;
	trackCheckoutFailed?: (error: unknown) => void;
}

export function ChangePlanButton({
	userId,
	organizationId,
	productId,
	className,
	targetPlanPrice = 0,
	trackCheckout,
	trackCheckoutFailed,
}: ChangePlanButtonProps) {
	const { purchases, activePlan } = usePurchases(organizationId);
	const [isLoading, setIsLoading] = useState(false);
	const queryClient = useQueryClient();
	const t = useTranslations();

	const changeSubscriptionPlanMutation = useChangeSubscriptionPlanMutation();
	const createCheckoutLinkMutation = useCreateCheckoutLinkMutation();
	// const createCustomerPortalMutation = useCreateCustomerPortalLinkMutation();

	// 确保purchases是数组
	const purchasesArray = Array.isArray(purchases) ? purchases : [];

	// Find the active subscription
	const activeSubscription = purchasesArray.find(
		(purchase: {
			type: string;
			status: string | null;
			subscriptionId: string | null;
			purchaseId?: string;
		}) =>
			purchase.type === "SUBSCRIPTION" &&
			purchase.status === "active" &&
			purchase.subscriptionId,
	);

	// Find a cancelled subscription
	const cancelledSubscription = purchasesArray.find(
		(purchase: {
			type: string;
			status: string | null;
			subscriptionId: string | null;
		}) =>
			purchase.type === "SUBSCRIPTION" &&
			purchase.status === "cancelled" &&
			purchase.subscriptionId,
	);

	const handleChangePlan = async () => {
		// Check if productId is valid
		if (!productId || productId.trim() === "") {
			toast.error("Invalid plan selected");
			return;
		}

		if (!(userId || organizationId)) {
			return;
		}

		setIsLoading(true);

		// 事件是「点击」本身，所以在请求之前就发出去，让 tracker 的 flush 与后端
		// 往返重叠，而不是叠在跳转前面。
		const tracked = trackCheckout?.();

		try {
			// 如果用户已有活跃订阅，直接调用change subscription plan API
			// 存量 Creem 订阅（sub_*）不走改套餐，直接新订 Waffo；激活后 webhook
			// 会自动把 Creem 那条期末取消。
			const isLegacyCreemPlan =
				activeSubscription?.subscriptionId?.startsWith("sub_");

			if (
				activeSubscription?.subscriptionId &&
				activePlan?.purchaseId &&
				!isLegacyCreemPlan
			) {
				console.log("Changing subscription plan via API");
				console.log(
					"subscriptionId:",
					activeSubscription.subscriptionId,
				);
				console.log("newProductId:", productId);

				// Determine if this is an upgrade or downgrade
				const currentPrice = activePlan?.price?.amount ?? 0;
				// If target price is explicitly provided, use it. Otherwise assume downgrade to be safe?
				// Actually, we pass targetPlanPrice from PricingTable.
				// Upgrade: Target Price > Current Price
				const isUpgrade = targetPlanPrice > currentPrice;

				console.log("Plan Change Logic:", {
					currentPrice,
					targetPlanPrice,
					isUpgrade,
					behavior: isUpgrade
						? "Immediate (Upgrade)"
						: "Deferred (Downgrade/Lateral)",
				});

				// 直接调用计划变更API
				const response =
					await changeSubscriptionPlanMutation.mutateAsync({
						subscriptionId: activeSubscription.subscriptionId,
						newProductId: productId,
						immediately: isUpgrade, // Pass dynamic flag
						redirectUrl: window.location.href,
					});

				// 后端返回正式换档确认页，付款成功后由 webhook 落库。
				if (response.checkoutUrl) {
					await tracked;
					window.location.href = response.checkoutUrl;
					return;
				}

				// 刷新购买数据以更新UI
				queryClient.invalidateQueries({
					queryKey: purchasesQueryKey(organizationId),
				});

				toast.success("Subscription plan changed successfully");
			} else {
				// 如果用户没有活跃订阅，创建新的 checkout
				// 使用干净的URL作为redirectUrl
				const cleanRedirectUrl = `${window.location.origin}/subscription`;
				const { checkoutLink } =
					await createCheckoutLinkMutation.mutateAsync({
						type: "subscription",
						productId,
						organizationId,
						redirectUrl: cleanRedirectUrl,
					});

				await tracked;
				window.location.href = checkoutLink;
			}
		} catch (error) {
			console.error("Change plan error:", error);
			trackCheckoutFailed?.(error);
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to change plan",
			);
		} finally {
			setIsLoading(false);
		}
	};

	// Disable the button if productId is not valid or if it's the same plan or if subscription is cancelled
	const isProductIdValid = productId && productId.trim() !== "";

	// 修复 isSamePlan 逻辑，确保只有当前计划被禁用
	// 使用 activePlan.price?.productId 与当前 productId 比较
	// 如果产品ID相同，说明是同一个产品，按钮应该禁用
	// 如果产品ID不同，即使是同一个计划的不同周期（月度/年度），按钮也应该可点击
	const isSamePlan = activePlan?.price?.productId === productId;

	// 添加调试日志
	console.log("ChangePlanButton debug info:", {
		productId,
		activeProductId: activePlan?.price?.productId,
		isSamePlan,
		isProductIdValid,
		isSubscriptionCancelled: Boolean(cancelledSubscription),
	});

	const isSubscriptionCancelled = Boolean(cancelledSubscription);

	// Determine the button text based on whether user has an active subscription
	const buttonText = activeSubscription?.subscriptionId
		? t("settings.billing.changePlan.title")
		: t("pricing.choosePlan");

	// Tooltip text to explain why button is disabled
	let tooltipText = "";
	if (isSubscriptionCancelled) {
		tooltipText = "Please resume your subscription before changing plans";
	} else if (isSamePlan) {
		tooltipText = "You are already subscribed to this plan";
	}

	return (
		<Button
			onClick={handleChangePlan}
			disabled={
				isLoading ||
				!isProductIdValid ||
				isSamePlan ||
				isSubscriptionCancelled
			}
			className={className}
			variant="outline"
			title={tooltipText}
		>
			{isLoading ? "Processing..." : buttonText}
		</Button>
	);
}
